import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import {
  ChevronLeft,
  ChevronRight,
  CircleAlert,
  Cloud,
  Download,
  HardDrive,
  Server,
  History,
  Pause,
  Play,
  RotateCcw,
  RotateCw,
  ShieldCheck,
  SkipBack,
  SkipForward,
} from 'lucide-react';
import { useCallback, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { Link } from 'react-router';

import { Button } from '../components/Button';
import { EmptyState } from '../components/EmptyState';
import { PageHeader } from '../components/PageHeader';
import { PhoneCollapse } from '../components/PhoneCollapse';
import { SearchInput } from '../components/SearchInput';
import { useAuth } from '../features/auth/AuthProvider';
import { holdsKey, listHolds } from '../features/evidence/api';
import { ExportDialog, ProtectDialog } from '../features/evidence/dialogs';
import { deviceKey, devicesKey, getDevice, listDevices } from '../features/inventory/api';
import { CameraTree } from '../features/live/CameraTree';
import {
  listRecordings,
  playbackKey,
  recordingsKey,
  segmentLocation,
  startPlayback,
} from '../features/playback/api';
import { mediaUrl } from '../features/playback/api';
import { Player, type PlayerState } from '../features/playback/Player';
import {
  coverage,
  locate,
  nextAfter,
  recordedMs,
  segmentNear,
  spanStartAfter,
  spanStartBefore,
  toSegs,
  type Seg,
} from '../features/playback/ranges';
import { Timeline } from '../features/playback/Timeline';
import { DeviceViewer } from '../features/playback/DeviceViewer';
import { EventJumps } from '../features/playback/EventJumps';
import { useEventMarks } from '../features/playback/marks';
import { zoomLevels, type ZoomId } from '../features/playback/zoom';
import { addDays, clock, dayOf, dayRange, isDay } from '../features/playback/time';
import { ApiError } from '../lib/api';
import { useUrlState } from '../lib/useUrlState';

const PAGE_SIZE = 50;
const rates = [0.5, 1, 2, 4, 8];

const dateLabel = (day: string) =>
  new Intl.DateTimeFormat('pt-BR', { dateStyle: 'full', timeZone: 'UTC' }).format(
    new Date(`${day}T12:00:00Z`),
  );

function formatDuration(ms: number) {
  const m = Math.round(ms / 60_000);
  if (m < 60) return `${String(m)} min`;
  return `${String(Math.floor(m / 60))} h ${String(m % 60).padStart(2, '0')} min`;
}

export function PlaybackPage() {
  const { request, session } = useAuth();
  const tz = session?.tenant.timezone;
  const [params, setParams] = useUrlState();
  // The page's notion of today is fixed when it opens.
  const [openedAt] = useState(() => Date.now());
  const today = dayOf(openedAt, tz);
  const deviceId = params.get('device') ?? '';
  // Phones: the camera list closes once a camera is chosen (PhoneCollapse).
  const [listOpen, setListOpen] = useState(!deviceId);
  // Recordings of the VMS, or those kept on the device itself (ADR-020).
  const source = params.get('source') === 'device' ? 'device' : 'server';
  const dayParam = params.get('day') ?? '';
  const day = isDay(dayParam) && dayParam <= today ? dayParam : today;
  const [q, setQ] = useState('');
  const range = useMemo(() => dayRange(day, tz), [day, tz]);

  const devices = useInfiniteQuery({
    queryKey: [...devicesKey, 'playback', q],
    initialPageParam: 1,
    queryFn: ({ pageParam }) =>
      listDevices(request, {
        q,
        site: '',
        kind: '',
        status: '',
        sort: 'name',
        page: pageParam,
        size: PAGE_SIZE,
        // DVR/NVR recorders group their channels; a search lists matching cameras.
        topLevel: !q,
        excludeRecorders: Boolean(q),
      }),
    getNextPageParam: (last, pages) =>
      pages.length * PAGE_SIZE < last.total ? pages.length + 1 : undefined,
  });
  const all = devices.data?.pages.flatMap((p) => p.items) ?? [];
  const listed = all.find((d) => d.id === deviceId);
  // A recorder's channel is not in the top-level list: load it by id.
  const fetched = useQuery({
    queryKey: deviceKey(deviceId),
    queryFn: () => getDevice(request, deviceId),
    enabled: deviceId !== '' && !listed && devices.isSuccess,
    staleTime: 30_000,
    retry: false,
  });
  const device = listed ?? fetched.data;

  return (
    <>
      <PageHeader
        title="Reprodução"
        description="Gravações por câmera e dia, no fuso horário da organização."
      />
      <div className="grid gap-4 p-4 sm:p-6 lg:grid-cols-[17rem_1fr]">
        <aside aria-label="Câmeras" className="space-y-2">
          <PhoneCollapse
            label="Câmeras"
            summary={device?.name}
            open={listOpen}
            onToggle={() => {
              setListOpen((o) => !o);
            }}
          >
            <SearchInput label="Buscar câmera" value={q} onChange={setQ} />
            <CameraTree
              devices={all}
              loading={devices.isPending}
              failed={devices.isError}
              filtered={q !== ''}
              selected={deviceId}
              selectedParent={device?.parent_id ?? null}
              onPick={(id) => {
                setParams({ device: id });
                setListOpen(false);
              }}
            />
            {devices.hasNextPage && (
              <Button
                className="w-full"
                disabled={devices.isFetchingNextPage}
                onClick={() => {
                  void devices.fetchNextPage();
                }}
              >
                {devices.isFetchingNextPage ? 'Carregando…' : 'Carregar mais'}
              </Button>
            )}
          </PhoneCollapse>
        </aside>

        <section aria-label="Reprodução" className="min-w-0 space-y-3">
          <DayBar
            day={day}
            today={today}
            onDay={(d) => {
              setParams({ day: d === today ? '' : d });
            }}
          />
          {deviceId && (
            <div role="radiogroup" aria-label="Origem das gravações" className="flex gap-1">
              {(
                [
                  ['server', 'Servidor (Pro Guard)'],
                  ['device', 'Equipamento (DVR/NVR)'],
                ] as const
              ).map(([value, label]) => (
                <Button
                  key={value}
                  role="radio"
                  aria-checked={source === value}
                  className={source === value ? 'border-accent! text-fg!' : ''}
                  onClick={() => {
                    setParams({ source: value === 'device' ? 'device' : '' });
                  }}
                >
                  {label}
                </Button>
              ))}
            </div>
          )}
          {deviceId && source === 'device' ? (
            <DeviceViewer
              isToday={day === today}
              key={`${deviceId}|${day}`}
              deviceId={deviceId}
              deviceName={device?.name ?? ''}
              range={range}
              timeZone={tz}
            />
          ) : deviceId ? (
            <Viewer
              key={`${deviceId}|${day}`}
              deviceId={deviceId}
              deviceName={device?.name ?? ''}
              day={day}
              range={range}
              isToday={day === today}
              timeZone={tz}
            />
          ) : (
            <div className="rounded-lg border border-line bg-surface">
              <EmptyState
                icon={History}
                title="Escolha uma câmera"
                description="Selecione uma câmera na lista para ver a linha do tempo das gravações do dia."
              />
            </div>
          )}
        </section>
      </div>
    </>
  );
}

function DayBar({ day, today, onDay }: { day: string; today: string; onDay: (d: string) => void }) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <Button
        aria-label="Dia anterior"
        onClick={() => {
          onDay(addDays(day, -1));
        }}
      >
        <ChevronLeft aria-hidden="true" className="size-4" />
      </Button>
      <input
        type="date"
        aria-label="Dia"
        value={day}
        max={today}
        onChange={(e) => {
          if (isDay(e.target.value)) onDay(e.target.value);
        }}
        className="h-8 rounded-md border border-line-strong bg-canvas px-2 font-mono text-[13px] text-fg"
      />
      <Button
        aria-label="Próximo dia"
        disabled={day >= today}
        onClick={() => {
          onDay(addDays(day, 1));
        }}
      >
        <ChevronRight aria-hidden="true" className="size-4" />
      </Button>
      {day !== today && (
        <Button
          onClick={() => {
            onDay(today);
          }}
        >
          Hoje
        </Button>
      )}
      <span className="text-[13px] text-fg-muted first-letter:uppercase">{dateLabel(day)}</span>
    </div>
  );
}

function Viewer({
  deviceId,
  deviceName,
  day,
  range,
  isToday,
  timeZone,
}: {
  deviceId: string;
  deviceName: string;
  day: string;
  range: [number, number];
  isToday: boolean;
  timeZone: string | undefined;
}) {
  const { request, can } = useAuth();
  const [from, to] = range;
  const recordings = useQuery({
    queryKey: recordingsKey(deviceId, from),
    queryFn: () => listRecordings(request, deviceId, from, to),
    // Today keeps growing: pick up new segments every minute.
    refetchInterval: isToday ? 60_000 : false,
  });
  const grant = useQuery({
    queryKey: playbackKey(deviceId, from),
    queryFn: () => startPlayback(request, deviceId, from, to),
    // Tokens last an hour; renew before they expire.
    staleTime: 50 * 60_000,
    refetchInterval: 50 * 60_000,
    // API answers (403, 503 without storage...) are final; only network failures retry.
    retry: (n, err) => !(err instanceof ApiError) && n < 2,
  });

  // Evidence holds of the day; without access or on failure the band is simply absent.
  const holds = useQuery({
    queryKey: [...holdsKey, 'day', deviceId, from],
    queryFn: () => listHolds(request, { deviceId, active: true, from, to, size: 200 }),
    retry: false,
  });
  const holdItems = holds.data?.items ?? [];
  const holdSpans = useMemo(
    () =>
      (holds.data?.items ?? []).map((h) => ({ start: Date.parse(h.from), end: Date.parse(h.to) })),
    [holds.data],
  );
  const [dialog, setDialog] = useState<'protect' | 'export' | null>(null);
  const segs = useMemo(() => toSegs(recordings.data ?? []), [recordings.data]);
  const byId = useMemo(
    () => new Map((recordings.data ?? []).map((s) => [s.id, s])),
    [recordings.data],
  );
  const spans = useMemo(() => coverage(segs), [segs]);
  const segmentAt = useCallback((t: number) => segmentNear(segs, t)?.id ?? null, [segs]);
  const video = useRef<HTMLVideoElement>(null);
  const [current, setCurrent] = useState<Seg | null>(null);
  const pendingOffset = useRef(0);
  const [playhead, setPlayhead] = useState<number | null>(null);
  const [state, setState] = useState<PlayerState>('idle');
  const [rate, setRate] = useState(1);
  const [zoom, setZoom] = useState<ZoomId>('24h');
  const marks = useEventMarks(deviceId, range, isToday);

  /** Plays from instant t: the segment containing it, or the next recorded one. */
  const go = (t: number) => {
    const clamped = Math.min(Math.max(t, from), to - 1000);
    const loc = locate(segs, clamped);
    if (!loc) {
      setPlayhead(clamped);
      setState('ended');
      return;
    }
    setPlayhead(loc.seg.start + loc.offsetMs);
    const el = video.current;
    if (current?.id === loc.seg.id && el && el.readyState >= 1) {
      el.currentTime = loc.offsetMs / 1000;
      void el.play().catch(() => {
        setState('paused');
      });
      return;
    }
    pendingOffset.current = loc.offsetMs / 1000;
    setCurrent(loc.seg);
    setState('loading');
  };
  const onLoaded = (el: HTMLVideoElement) => {
    el.currentTime = pendingOffset.current;
    el.playbackRate = rate;
    pendingOffset.current = 0;
    void el.play().catch(() => {
      setState('paused');
    });
  };
  const onEnded = () => {
    const next = current ? nextAfter(segs, current.id) : null;
    if (!next) {
      setState('ended');
      return;
    }
    pendingOffset.current = 0;
    setCurrent(next);
    setPlayhead(next.start);
  };
  const toggle = () => {
    const el = video.current;
    if (!el || state === 'idle' || state === 'ended') {
      const firstSpan = spans[0];
      if (firstSpan) go(playhead ?? firstSpan.start);
      return;
    }
    if (el.paused) void el.play();
    else el.pause();
  };
  const onKey = (e: KeyboardEvent<HTMLDivElement>) => {
    // Shortcuts never apply while typing or inside a dialog (e.g. the evidence form).
    const el = e.target;
    if (
      el instanceof HTMLInputElement ||
      el instanceof HTMLSelectElement ||
      el instanceof HTMLTextAreaElement ||
      (el instanceof Element && el.closest('[role="dialog"]'))
    )
      return;
    if (e.key === ' ' || e.key === 'k') {
      e.preventDefault();
      toggle();
    } else if ((e.key === 'j' || e.key === 'l') && playhead !== null) {
      go(playhead + (e.key === 'j' ? -10_000 : 10_000));
    }
  };

  if (recordings.isPending) {
    return <div aria-hidden="true" className="aspect-video animate-pulse rounded-lg bg-surface" />;
  }
  if (recordings.isError) {
    return (
      <div className="rounded-lg border border-line bg-surface">
        <EmptyState
          tone="crit"
          icon={CircleAlert}
          title="Não foi possível carregar as gravações"
          description="A lista de gravações deste dia não pôde ser obtida."
          action={
            <Button
              variant="primary"
              onClick={() => {
                void recordings.refetch();
              }}
            >
              Tentar novamente
            </Button>
          }
        />
      </div>
    );
  }
  const unavailable =
    grant.error instanceof ApiError && grant.error.code === 'recording_unavailable';
  if (unavailable) {
    return (
      <div className="rounded-lg border border-line bg-surface">
        <EmptyState
          icon={CircleAlert}
          title="Gravação não configurada"
          description="Esta instalação não tem armazenamento de gravação (RECORDING_DIR)."
        />
      </div>
    );
  }

  const recorded = recordedMs(spans);
  const gaps = Math.max(spans.length - 1, 0);
  const last = spans.at(-1);
  const prevSpan = playhead !== null ? spanStartBefore(spans, playhead) : null;
  const nextSpan = playhead !== null ? spanStartAfter(spans, playhead) : null;
  // Where the segment on screen is kept: the server's disk or the bucket.
  const currentSeg = current ? byId.get(current.id) : undefined;
  const location = currentSeg ? segmentLocation(currentSeg) : null;

  return (
    <div className="space-y-3" onKeyDown={onKey}>
      <div className="relative aspect-video overflow-hidden rounded-lg border border-line bg-black">
        <Player
          video={video}
          src={current && grant.data ? mediaUrl(current.id, grant.data.token) : undefined}
          onTime={(sec) => {
            if (current) setPlayhead(current.start + sec * 1000);
          }}
          onLoaded={onLoaded}
          onEnded={onEnded}
          onState={setState}
        />
        <Overlay
          state={state}
          empty={segs.length === 0}
          grantFailed={grant.isError}
          onNode={location?.kind === 'node'}
          name={deviceName}
          onStart={() => {
            const s = spans[0];
            if (s) go(s.start);
          }}
          onLatest={() => {
            if (last) go(Math.max(last.start, last.end - 5 * 60_000));
          }}
        />
        {location && (
          <div
            title={location.detail}
            aria-label={`Origem do trecho: ${location.detail}`}
            className="pointer-events-none absolute top-2 left-2 flex items-center gap-1.5 rounded bg-black/70 px-2 py-0.5 text-xs text-white"
          >
            {location.kind === 'cloud' ? (
              <Cloud aria-hidden="true" className="size-3.5 text-accent" />
            ) : location.kind === 'node' ? (
              <Server aria-hidden="true" className="size-3.5 text-warn" />
            ) : (
              <HardDrive aria-hidden="true" className="size-3.5 text-white/80" />
            )}
            {location.label}
          </div>
        )}
        {playhead !== null && (
          <div className="pointer-events-none absolute top-2 right-2 rounded bg-black/70 px-2 py-0.5 font-mono text-xs text-white">
            {clock(playhead, timeZone)}
          </div>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Button
          variant="primary"
          aria-label={state === 'playing' ? 'Pausar' : 'Reproduzir'}
          disabled={segs.length === 0 || !grant.data}
          onClick={toggle}
        >
          {state === 'playing' ? (
            <Pause aria-hidden="true" className="size-4" />
          ) : (
            <Play aria-hidden="true" className="size-4" />
          )}
        </Button>
        <Button
          aria-label="Voltar 10 segundos"
          disabled={playhead === null}
          onClick={() => {
            if (playhead !== null) go(playhead - 10_000);
          }}
        >
          <RotateCcw aria-hidden="true" className="size-4" />
        </Button>
        <Button
          aria-label="Avançar 10 segundos"
          disabled={playhead === null}
          onClick={() => {
            if (playhead !== null) go(playhead + 10_000);
          }}
        >
          <RotateCw aria-hidden="true" className="size-4" />
        </Button>
        <Button
          aria-label="Trecho anterior"
          disabled={prevSpan === null}
          onClick={() => {
            if (prevSpan !== null) go(prevSpan);
          }}
        >
          <SkipBack aria-hidden="true" className="size-4" />
        </Button>
        <Button
          aria-label="Próximo trecho"
          disabled={nextSpan === null}
          onClick={() => {
            if (nextSpan !== null) go(nextSpan);
          }}
        >
          <SkipForward aria-hidden="true" className="size-4" />
        </Button>
        <EventJumps marks={marks} playhead={playhead} dayStart={range[0]} onSeek={go} />
        <label className="flex items-center gap-1.5 text-xs text-fg-muted">
          Velocidade
          <select
            value={String(rate)}
            onChange={(e) => {
              const r = Number(e.target.value);
              setRate(r);
              if (video.current) video.current.playbackRate = r;
            }}
            className="h-8 rounded-md border border-line-strong bg-canvas px-1.5 text-[13px] text-fg"
          >
            {rates.map((r) => (
              <option key={r} value={String(r)}>
                {String(r).replace('.', ',')}×
              </option>
            ))}
          </select>
        </label>
        {can('recordings.protect') && (
          <Button
            onClick={() => {
              setDialog('protect');
            }}
          >
            <ShieldCheck aria-hidden="true" className="size-4 text-warn" />
            Proteger evidência
          </Button>
        )}
        {can('recordings.export') && (
          <Button
            disabled={segs.length === 0}
            onClick={() => {
              setDialog('export');
            }}
          >
            <Download aria-hidden="true" className="size-4" />
            Exportar
          </Button>
        )}
        <div role="group" aria-label="Zoom da linha do tempo" className="ml-auto flex gap-1">
          {zoomLevels.map((z) => (
            <Button
              key={z.id}
              aria-pressed={zoom === z.id}
              className={zoom === z.id ? 'border-accent! text-fg!' : ''}
              onClick={() => {
                setZoom(z.id);
              }}
            >
              {z.label}
            </Button>
          ))}
        </div>
      </div>

      <Timeline
        day={range}
        spans={spans}
        holds={holdSpans}
        marks={marks}
        playhead={playhead}
        zoom={zoom}
        timeZone={timeZone}
        onSeek={go}
        segmentAt={segmentAt}
      />
      <p className="text-xs text-fg-muted" aria-live="polite">
        {segs.length === 0
          ? 'Nenhuma gravação neste dia.'
          : `${formatDuration(recorded)} gravados · ${String(gaps)} ${
              gaps === 1 ? 'interrupção' : 'interrupções'
            } · atalhos: espaço reproduz/pausa, J/L ±10 s, setas na linha do tempo ±1 min`}
      </p>
      {holdItems.length > 0 && (
        <section
          aria-label="Evidências neste dia"
          className="rounded-md border border-warn/30 bg-warn-soft px-3 py-2"
        >
          <ul className="space-y-1 text-xs text-fg-muted">
            {holdItems.map((h) => (
              <li key={h.id} className="flex items-start gap-2">
                <ShieldCheck aria-hidden="true" className="mt-0.5 size-3.5 shrink-0 text-warn" />
                <span className="min-w-0">
                  <span className="font-mono text-fg">
                    {clock(Date.parse(h.from), timeZone)}–{clock(Date.parse(h.to), timeZone)}
                  </span>{' '}
                  · {h.reason} · por {h.created_by.name}
                </span>
              </li>
            ))}
          </ul>
          <Link to="/evidence" className="mt-1 inline-block text-xs text-accent hover:underline">
            Ver todas as evidências
          </Link>
        </section>
      )}
      {dialog && (
        <DialogFor
          kind={dialog}
          deviceId={deviceId}
          deviceName={deviceName}
          day={day}
          dayRange={range}
          initial={defaultHoldRange(playhead, spans.at(-1) ?? null, range)}
          timeZone={timeZone}
          onClose={() => {
            setDialog(null);
          }}
        />
      )}
    </div>
  );
}

function DialogFor({
  kind,
  ...props
}: { kind: 'protect' | 'export' } & Parameters<typeof ProtectDialog>[0]) {
  return kind === 'protect' ? <ProtectDialog {...props} /> : <ExportDialog {...props} />;
}

/** Ten minutes around the playhead, else the last recorded ten minutes, within the day. */
function defaultHoldRange(
  playhead: number | null,
  last: { end: number } | null,
  [dayFrom, dayTo]: [number, number],
): [number, number] {
  const sec = (t: number) => Math.floor(t / 1000) * 1000;
  let a: number;
  let b: number;
  if (playhead !== null) [a, b] = [playhead - 5 * 60_000, playhead + 5 * 60_000];
  else if (last) [a, b] = [last.end - 10 * 60_000, last.end];
  else [a, b] = [dayFrom, dayFrom + 10 * 60_000];
  return [sec(Math.max(a, dayFrom)), sec(Math.min(b, dayTo - 1000))];
}

function Overlay({
  state,
  empty,
  grantFailed,
  onNode,
  name,
  onStart,
  onLatest,
}: {
  state: PlayerState;
  empty: boolean;
  grantFailed: boolean;
  onNode: boolean;
  name: string;
  onStart: () => void;
  onLatest: () => void;
}) {
  let body: React.ReactNode = null;
  if (empty) {
    body = (
      <p className="text-sm text-white/80">
        Nenhuma gravação de {name || 'esta câmera'} neste dia.
      </p>
    );
  } else if (grantFailed) {
    body = <p className="text-sm text-white/80">Não foi possível autorizar a reprodução.</p>;
  } else if (state === 'error') {
    body = (
      <p className="text-sm text-white/80">
        {onNode
          ? 'Trecho guardado na máquina de vídeo, que pode não estar respondendo agora. Tente de novo em instantes.'
          : 'Este trecho não pôde ser carregado. Escolha outra posição na linha do tempo.'}
      </p>
    );
  } else if (state === 'ended') {
    body = <p className="text-sm text-white/80">Fim das gravações deste dia.</p>;
  } else if (state === 'idle') {
    body = (
      <div className="flex flex-wrap justify-center gap-2">
        <Button variant="primary" onClick={onStart}>
          <Play aria-hidden="true" className="size-4" /> Desde o início do dia
        </Button>
        <Button onClick={onLatest}>Últimos 5 minutos</Button>
      </div>
    );
  } else if (state === 'loading') {
    body = <p className="text-sm text-white/70">Carregando…</p>;
  }
  if (!body) return null;
  return (
    <div className="absolute inset-0 grid place-items-center bg-black/40 p-4 text-center">
      {body}
    </div>
  );
}
